import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pencil, Plus } from "lucide-react";
import { toast } from "react-toastify";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import PageHeader from "src/components/common/PageHeader";
import Button from "src/components/ui/Button";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as transactionActions from "src/redux/slices/transactionSlice";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";

const TransactionsListPage: React.FC = () => {
  const [limit] = useState(10);

  const { editTransaction, transactionsList } = useAppSelector(
    (state) => state.transaction
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    getTransactionList(1);
  }, []);

  useEffect(() => {
    if (editTransaction.data) {
      toast.success("Transaction updated successfully");
      dispatch(transactionActions.resetEditTransaction());
      getTransactionList(1);
    } else if (editTransaction.error) {
      toast.error(`Error: ${editTransaction.error}`);
    }
  }, [editTransaction]);

  const getTransactionList = (page: number) =>
    dispatch(transactionActions.getTransactionListRequest({ page, limit }));

  const handlePageChange = (page: number) => {
    getTransactionList(page);
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Transactions | Invyntra",
        description:
          "Track and manage all transactions with detailed information.",
        keywords: "transactions, details, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Transactions"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Transactions", path: "/admin/transaction/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Transactions"
            description="Manage all transactions here"
            actions={
              <Button onClick={() => navigate("/admin/transaction/create")}>
                <Plus size={16} className="mr-1" /> Add Transaction
              </Button>
            }
          />

          {/* Table */}
          <TableV2
            columns={[
              {
                key: "transactionId",
                header: "Transaction Id",
                accessor: ({ row, key }) => (
                  // <div className="w-32">#{row?.transactionId}</div>
                  <Link
                    className="underline"
                    to={`/admin/transaction/${
                      row?.transactionId || row?._id
                    }/details`}
                  >
                    #{row[key]}
                  </Link>
                ),
              },
              {
                key: "referenceId",
                header: "Reference Id",
                accessor: ({ row }) => (
                  <div className="w-40">{row?.referenceId || "-"}</div>
                ),
              },
              {
                key: "amount",
                header: "Amount",
                align: "right",
                accessor: ({ key, row }) => (
                  <div>{currencyFormatter(row[key])}</div>
                ),
              },
              {
                key: "transactionType",
                header: "Type",
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={
                      row?.[key] === "success"
                        ? "green"
                        : row?.[key] === "processing"
                        ? "yellow"
                        : row?.[key] === "created"
                        ? "blue"
                        : "red"
                    }
                  />
                ),
              },
              {
                key: "createdAt",
                header: "Created On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              // {
              //   key: "currentPlanDetails",
              //   header: "Plan Details",
              //   accessor: ({ key, row }) =>
              //     row?.[key]?.planId === "trial" ? (
              //       <Badge text={row?.[key]?.planId || ""} color={"yellow"} />
              //     ) : (
              //       row?.[key]?.planName
              //     ),
              // },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ key, row }) => (
                  <div className="pr-2">
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() =>
                        navigate(`/admin/transaction/${row._id}/edit`)
                      }
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={transactionsList?.data?.data}
            pagination={transactionsList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default TransactionsListPage;
